import * as Sentry from "@sentry/browser";
import { Chart, registerables } from "chart.js";
import * as regression from "regression";

const DEBUG_STEPS = false;

/**
 * The StepDetector class is responsible for detecting the number of steps taken
 * based on foot positions in Bird's Eye View (BEV). It can optionally calculate
 * gait speed, leveraging data from a depth model if enabled.
 */
export class StepDetector {
  /**
   * Constructs a new StepDetector.
   * Initializes references to Canvas contexts and Sentry logging.
   */
  constructor() {
    Chart.register(...registerables);

    // Retrieve the 2D rendering contexts for two charts
    this.bev_y_ctx = document.getElementById("chart-bev-y").getContext("2d");
    this.slope_ctx = document.getElementById("chart-slope").getContext("2d");

    // Construct a Sentry prefix for logging
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let assessment = sessionStorage.getItem("selectedAssesment");
    this.sentry_prefix =
      `${assessment === "22" ? "Gait_" : "TUG_"}${patientServerID}_` +
      new Date().toISOString();
  }

  /**
   * Private helper method that finds indices of local maxima in a numeric array.
   * @param {number[]} x - The numeric array to analyze.
   * @returns {number[]} Indices of local maxima.
   */
  __local_maxima(x) {
    let midpoints = [];
    let left_edges = [];
    let right_edges = [];
    let m = 0;

    let i = 1;
    let i_max = x.length - 1;
    while (i < i_max) {
      if (x[i - 1] < x[i]) {
        let i_ahead = i + 1;
        while (i_ahead < i_max && x[i_ahead] === x[i]) {
          i_ahead++;
        }
        if (x[i_ahead] < x[i]) {
          left_edges.push(i);
          right_edges.push(i_ahead - 1);
          midpoints.push(Math.floor((left_edges[m] + right_edges[m]) / 2));
          m++;
          i = i_ahead;
        }
      }
      i++;
    }
    return midpoints;
  }

  /**
   * Finds peaks in a numeric array above a specified height and separated by a minimum distance.
   * @param {number[]} array - The numeric data to find peaks in.
   * @param {number} minHeight - The minimum value a peak must have to be considered.
   * @param {number} minDistance - The minimum index distance between peaks.
   * @returns {number[]} The indices in `array` that are considered valid peaks.
   */
  findPeaks(array, minHeight, minDistance) {
    let peaks = this.__local_maxima(array);

    // Filter out peaks that are below the minHeight
    let filtered_peaks = peaks.filter((peak) => {
      return array[peak] >= minHeight;
    });

    // Extract the actual numeric values of these peaks
    let filtered_values = filtered_peaks.map((peak) => {
      return array[peak];
    });

    // Enforce min distance between peaks
    return this.select_by_distance(
      filtered_peaks,
      filtered_values,
      minDistance
    );
  }

  /**
   * Filters peaks to enforce a minimum distance between them, retaining
   * the peaks with higher values when conflicts arise.
   * @param {number[]} peaks - The indices of detected peaks.
   * @param {number[]} priority - The values at those peak indices.
   * @param {number} distance - The minimum spacing between peaks.
   * @returns {number[]} A reduced set of peak indices satisfying the distance constraint.
   */
  select_by_distance(peaks, priority, distance) {
    let peaks_size = peaks.length;
    let distance_ = Math.ceil(distance);
    let keep = Array(peaks_size).fill(1);

    let priority_to_position = peaks
      .map((_, i) => i)
      .sort((a, b) => priority[a] - priority[b]);

    for (let i = peaks_size - 1; i >= 0; i--) {
      let j = priority_to_position[i];
      if (keep[j] === 0) {
        continue;
      }

      let k = j - 1;
      while (k >= 0 && peaks[j] - peaks[k] < distance_) {
        keep[k] = 0;
        k--;
      }

      k = j + 1;
      while (k < peaks_size && peaks[k] - peaks[j] < distance_) {
        keep[k] = 0;
        k++;
      }
    }

    // Filter to keep only those peaks that survived
    let filtered = peaks.filter((_, i) => keep[i] === 1);
    return filtered;
  }

  /**
   * Computes the median of a numeric array.
   * @param {number[]} values - The array of numeric values.
   * @returns {number} The median of the array, or 0 if empty.
   */
  median(values) {
    if (values.length === 0) return 0;
    values.sort((a, b) => a - b);

    let half = Math.floor(values.length / 2);
    if (values.length % 2) {
      return values[half];
    }
    return (values[half - 1] + values[half]) / 2.0;
  }

  /**
   * Retrieves a depth distance from a depth data array at integer (x, y) coordinates,
   * offset by a partial scan region. Returns 0 if invalid or out of range.
   * @param {object} depthData - The depth data object from a .npy file (with shape/data).
   * @param {number} x - The x-coordinate in the partial depth map.
   * @param {number} y - The y-coordinate in the partial depth map.
   * @returns {number} The distance value from the depth map at (x, y), or 0 if invalid.
   */
  get_depth_data(depthData, x, y) {
    x = Math.round(x);
    y = Math.round(y);

    if (!depthData || !depthData.data) return 0;

    const partialRows = depthData.shape[0];
    const cols = depthData.shape[1];

    // Depth data starts at y=1000 in the original coordinate system
    if (y < 1000) {
      return 0;
    }

    const partialY = y - 1000;
    if (partialY < 0 || partialY >= partialRows) return 0;
    if (x < 0 || x >= cols) return 0;

    const idx = partialY * cols + x;
    return depthData.data[idx];
  }

  /**
   * Calculates the average of the middle fraction (e.g., 50%) of sorted values,
   * discarding high and low outliers based on the fraction parameter.
   * @param {number[]} values - An array of numeric values.
   * @param {number} [fraction=0.50] - The fraction of middle values to keep.
   * @returns {number} The average of the retained middle portion, or 0 if none.
   */
  averageMiddle(values, fraction = 0.5) {
    if (!values.length) return 0;
    values.sort((a, b) => a - b);
    let removeCount = Math.floor(((1 - fraction) / 2) * values.length);
    let sliced = values.slice(removeCount, values.length - removeCount);
    let sum = sliced.reduce((acc, val) => acc + val, 0);
    return sliced.length ? sum / sliced.length : 0;
  }

  /**
   * Main step counting function. Processes a series of foot data points and optionally
   * uses depth data to compute gait speed. Returns both the number of steps and the gait speed.
   * @param {object[]} feet_data - Array of foot position data in Bird's Eye View (BEV).
   * @param {object[]} feet_data_cam - Array of foot position data in camera coordinates.
   * @param {object} depthData - Depth data object (if available).
   * @returns {{step_count: number, gait_speed: number}} An object containing
   * the number of steps and the computed gait speed (m/s).
   */
  count_steps(feet_data, feet_data_cam, depthData) {
    if (feet_data < 50) {
      return {
        step_count: 0,
        gait_speed: 0,
      };
    }

    // Preprocess the foot data
    this.pre_process_data(feet_data);

    // Optional debugging: draw Y-coordinates chart
    if (DEBUG_STEPS) {
      let config = {
        type: "line",
        data: {
          labels: feet_data.map((data) => data.elapsedTime),
          datasets: [
            {
              label: "Left Foot BEV Y",
              data: feet_data.map((data) => data.left_foot_bev.y),
              pointRadius: 0,
              backgroundColor: "rgba(255, 99, 132, 0.2)",
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 1,
            },
            {
              label: "Right Foot BEV Y",
              data: feet_data.map((data) => data.right_foot_bev.y),
              pointRadius: 0,
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: true,
              text: "Y-Coordinate of Feet in BEV",
            },
          },
        },
      };
      new Chart(this.bev_y_ctx, config);
    }

    // Calculate slope of foot movement
    this.calculate_slope_at_each_point(feet_data);

    // Find peaks in slope for left and right feet
    let left_peaks = this.findPeaks(
      feet_data.map((data) => data.left_foot_bev_slope),
      5,
      5
    );
    let right_peaks = this.findPeaks(
      feet_data.map((data) => data.right_foot_bev_slope),
      5,
      5
    );

    // Suppress false peaks by comparing slopes
    left_peaks = left_peaks.filter((peak) => {
      return (
        feet_data[peak].left_foot_bev_slope >
        feet_data[peak].right_foot_bev_slope
      );
    });
    right_peaks = right_peaks.filter((peak) => {
      return (
        feet_data[peak].right_foot_bev_slope >
        feet_data[peak].left_foot_bev_slope
      );
    });

    let { steps, left_step_idxs, right_step_idxs } = this.detect_steps(
      left_peaks,
      right_peaks
    );

    // Gather timestamps for each detected step (for step time variability) without mutating the original arrays
    let step_timestamps = [];
    let left_idxs_copy = left_step_idxs.slice();
    let right_idxs_copy = right_step_idxs.slice();
    let left_ptr = 0;
    let right_ptr = 0;
    for (let i = 0; i < steps.length; i++) {
      let idx = null;
      if (steps[i] === "left" && left_ptr < left_idxs_copy.length) {
        idx = left_idxs_copy[left_ptr++];
      } else if (steps[i] === "right" && right_ptr < right_idxs_copy.length) {
        idx = right_idxs_copy[right_ptr++];
      }
      if (
        idx !== null &&
        feet_data[idx] &&
        typeof feet_data[idx].elapsedTime === "number"
      ) {
        step_timestamps.push(feet_data[idx].elapsedTime);
      }
    }

    // If no steps detected, return 0
    if (steps.length === 0) {
      return {
        step_count: steps.length,
        gait_speed: 0,
      };
    }

    console.log(steps);

    // Derive starting and ending foot positions to compute distance/time
    let first_statioinary_step = { x: 0, y: 0 };
    let first_statioinary_step_cam = { x: 0, y: 0 };

    // Check which foot is leading at the start
    if (feet_data_cam[0].right_foot_bev.y > feet_data_cam[0].left_foot_bev.y) {
      first_statioinary_step = {
        x: feet_data[0].elapsedTime,
        y: feet_data[0].right_foot_bev.y,
      };
      first_statioinary_step_cam = {
        y: feet_data_cam[0].right_foot_bev.y,
        x: feet_data_cam[0].right_foot_bev.x,
      };
    } else {
      first_statioinary_step = {
        x: feet_data[0].elapsedTime,
        y: feet_data[0].left_foot_bev.y,
      };
      first_statioinary_step_cam = {
        y: feet_data_cam[0].left_foot_bev.y,
        x: feet_data_cam[0].left_foot_bev.x,
      };
    }

    // Determine last stationary foot
    let last_stationary_step = { x: 0, y: 0 };
    let last_stationary_step_cam = { x: 0, y: 0 };

    if (steps[steps.length - 1] === "left") {
      let step_idx = left_step_idxs[left_step_idxs.length - 1];
      last_stationary_step = {
        x: feet_data[step_idx].elapsedTime,
        y: feet_data[step_idx].right_foot_bev.y,
      };
      last_stationary_step_cam = {
        y: feet_data_cam[step_idx].right_foot_bev.y,
        x: feet_data_cam[step_idx].right_foot_bev.x,
      };
    } else {
      let step_idx = right_step_idxs[right_step_idxs.length - 1];
      last_stationary_step = {
        x: feet_data[step_idx].elapsedTime,
        y: feet_data[step_idx].left_foot_bev.y,
      };
      last_stationary_step_cam = {
        y: feet_data_cam[step_idx].left_foot_bev.y,
        x: feet_data_cam[step_idx].left_foot_bev.x,
      };
    }

    // If the final time isn't after the start, speed can't be calculated
    if (last_stationary_step.x - first_statioinary_step.x <= 0) {
      return {
        step_count: steps.length,
        gait_speed: 0,
      };
    }

    // Gait distance in pixels and time in ms
    let gait_distance_px = last_stationary_step.y - first_statioinary_step.y;
    let gait_time_ms = last_stationary_step.x - first_statioinary_step.x;

    this.log_to_sentry(
      `First Step px: {${first_statioinary_step.y}, ${first_statioinary_step.x} }, Last step  { ${last_stationary_step.y}, ${last_stationary_step.x}}`
    );
    this.log_to_sentry(
      `Gait Distance: ${gait_distance_px}, Gait Time: ${gait_time_ms}`
    );

    console.log("Gait Distance: " + gait_distance_px);
    console.log("Gait Time: " + gait_time_ms);

    const gait_time_sec = gait_time_ms / 1000;

    // Sample around the foot positions for a more stable depth measure
    let offsetRange = 10;

    let xValsFirst = [];
    for (let offset = -offsetRange; offset <= offsetRange; offset++) {
      xValsFirst.push(first_statioinary_step_cam.x + offset);
    }
    let depthValsFirst = xValsFirst.map((x) =>
      this.get_depth_data(depthData, x, first_statioinary_step_cam.y)
    );
    let depthValue1 = this.averageMiddle(depthValsFirst, 0.3);

    let xValsLast = [];
    for (let offset = -offsetRange; offset <= offsetRange; offset++) {
      xValsLast.push(last_stationary_step_cam.x + offset);
    }
    let depthValsLast = xValsLast.map((x) =>
      this.get_depth_data(depthData, x, last_stationary_step_cam.y)
    );
    let depthValue2 = this.averageMiddle(depthValsLast, 0.3);

    // Speed (m/s)
    let gait_speed = (depthValue1 - depthValue2) / gait_time_sec;

    // Calculate average step length for left and right
    let left_step_lengths = [];
    for (let i = 1; i < left_step_idxs.length; i++) {
      let prevIdx = left_step_idxs[i - 1];
      let currIdx = left_step_idxs[i];
      let dist = Math.abs(
        feet_data[currIdx].left_foot_bev.y - feet_data[prevIdx].left_foot_bev.y
      );
      left_step_lengths.push(dist);
    }
    let right_step_lengths = [];
    for (let i = 1; i < right_step_idxs.length; i++) {
      let prevIdx = right_step_idxs[i - 1];
      let currIdx = right_step_idxs[i];
      let dist = Math.abs(
        feet_data[currIdx].right_foot_bev.y -
          feet_data[prevIdx].right_foot_bev.y
      );
      right_step_lengths.push(dist);
    }
    let left_step_length = left_step_lengths.length
      ? left_step_lengths.reduce((a, b) => a + b, 0) / left_step_lengths.length
      : null;
    let right_step_length = right_step_lengths.length
      ? right_step_lengths.reduce((a, b) => a + b, 0) /
        right_step_lengths.length
      : null;

    // Average step length (all steps)
    let all_step_lengths = left_step_lengths.concat(right_step_lengths);
    let avg_step_length = all_step_lengths.length
      ? all_step_lengths.reduce((a, b) => a + b, 0) / all_step_lengths.length
      : null;

    // Stride length: total distance / (number of steps / 2)
    // let stride_length = null;
    // if (all_step_lengths.length > 0 && gait_distance_px > 0) {
    //   stride_length = gait_distance_px / (steps.length / 2);
    // }

    // Optional debugging: slope chart
    if (DEBUG_STEPS) {
      let left_step_chart_data = left_step_idxs.map((step) => {
        return {
          x: feet_data[step].elapsedTime,
          y: feet_data[step].left_foot_bev_slope,
        };
      });

      let right_step_chart_data = right_step_idxs.map((step) => {
        return {
          x: feet_data[step].elapsedTime,
          y: feet_data[step].right_foot_bev_slope,
        };
      });

      let config = {
        type: "line",
        data: {
          labels: feet_data.map((data) => data.elapsedTime),
          datasets: [
            {
              label: "Left Foot Slope",
              data: feet_data.map((data) => data.left_foot_bev_slope),
              pointRadius: 0,
              backgroundColor: "rgba(255, 99, 132, 0.2)",
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 1,
            },
            {
              label: "Right Foot Slope",
              data: feet_data.map((data) => data.right_foot_bev_slope),
              pointRadius: 0,
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
            {
              label: "Left Steps",
              data: left_step_chart_data,
              type: "scatter",
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 5,
            },
            {
              label: "Right Steps",
              data: right_step_chart_data,
              type: "scatter",
              backgroundColor: "rgba(255, 99, 132, 0.2)",
              borderColor: "rgba(255, 99, 132, 0.2)",
              borderWidth: 5,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: true,
              text: "Slope of Feet in BEV",
            },
          },
        },
      };
      new Chart(this.slope_ctx, config);
    }

    return {
      step_count: steps.length,
      gait_speed: gait_speed.toFixed(2),
      left_step_length,
      right_step_length,
      avg_step_length,
      // stride_length,
      step_timestamps,
    };
  }

  /**
   * Given the indices of detected peaks for left and right feet, merges them into a
   * unified step list preserving order. This helps determine the sequence of steps.
   * @param {number[]} left_peaks - Array of indices where the left foot slope peaks.
   * @param {number[]} right_peaks - Array of indices where the right foot slope peaks.
   * @returns {{steps: string[], left_step_idxs: number[], right_step_idxs: number[]}}
   * An object containing the sorted step sequence and foot indices for left and right steps.
   */
  detect_steps(left_peaks, right_peaks) {
    let left_ptr = 0;
    let right_ptr = 0;
    let steps = ["None"];
    let left_step_idxs = [];
    let right_step_idxs = [];

    while (left_ptr < left_peaks.length && right_ptr < right_peaks.length) {
      let left_step_idx = left_peaks[left_ptr];
      let right_step_idx = right_peaks[right_ptr];

      if (left_step_idx < right_step_idx) {
        if (steps[steps.length - 1] !== "left") {
          steps.push("left");
          left_step_idxs.push(left_step_idx);
        }
        left_ptr++;
      } else {
        if (steps[steps.length - 1] !== "right") {
          steps.push("right");
          right_step_idxs.push(right_step_idx);
        }
        right_ptr++;
      }
    }

    // Remaining leftover left or right peaks
    if (left_ptr < left_peaks.length && steps[steps.length - 1] !== "left") {
      steps.push("left");
      left_step_idxs.push(left_peaks[left_ptr]);
    }
    if (right_ptr < right_peaks.length && steps[steps.length - 1] !== "right") {
      steps.push("right");
      right_step_idxs.push(right_peaks[right_ptr]);
    }

    // Remove the initial "None" placeholder
    return {
      steps: steps.slice(1),
      left_step_idxs: left_step_idxs,
      right_step_idxs: right_step_idxs,
    };
  }

  /**
   * Computes a local slope for both left and right foot positions using a sliding window regression.
   * @param {object[]} feet_data - The array of foot data to update in-place with slope values.
   */
  calculate_slope_at_each_point(feet_data) {
    const window_size = 7;
    const half_size = Math.floor(window_size / 2);

    feet_data.forEach((row, i) => {
      let leftFootBevY = [];
      let rightFootBevY = [];

      // Build arrays of y-values around the current index
      for (let j = i - half_size; j <= i + half_size; j++) {
        if (feet_data[j]) {
          leftFootBevY.push(feet_data[j].left_foot_bev.y);
          rightFootBevY.push(feet_data[j].right_foot_bev.y);
        }
      }

      // Perform a linear regression on left foot
      let poly = regression.linear(leftFootBevY.map((y, idx) => [idx, y]));
      row.left_foot_bev_slope = -1 * poly.equation[0];

      // Perform a linear regression on right foot
      poly = regression.linear(rightFootBevY.map((y, idx) => [idx, y]));
      row.right_foot_bev_slope = -1 * poly.equation[0];
    });
  }

  /**
   * Preprocesses the foot data array:
   *  1) Computes elapsedTime for each data row relative to the first timestamp.
   *  2) Performs a median filter on left_foot_bev.y and right_foot_bev.y using a window of 5 points.
   * @param {object[]} feet_data - The foot data array to be updated in-place.
   */
  pre_process_data(feet_data) {
    feet_data.forEach((data, i) => {
      // Time offset from the start
      data.elapsedTime = data.timestamp - feet_data[0].timestamp;

      // Median filtering to stabilize y-values
      let left_feet_y = [];
      let right_feet_y = [];
      for (let j = i - 2; j <= i + 2; j++) {
        if (feet_data[j]) {
          left_feet_y.push(feet_data[j].left_foot_bev.y);
          right_feet_y.push(feet_data[j].right_foot_bev.y);
        }
      }
      data.left_foot_bev.y = this.median(left_feet_y);
      data.right_foot_bev.y = this.median(right_feet_y);
    });
  }

  /**
   * Logs a message to Sentry, prefixing it with an identifier for the current session.
   * @param {string} message - The message string to log.
   */
  log_to_sentry(message) {
    Sentry.captureMessage(this.sentry_prefix + message);
  }
}
